import React from 'react';
import {
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Box,
  Chip,
  Typography,
  useTheme,
} from '@mui/material';
// import { SEPSearchRow } from '../../../shared/types/SEP';
// import SepTableRow from './SepTableRow';
import { HeadCell } from './SepTableBody';
import SortIcons from './SortIcon';
import OnTheClockIcon from '../../assets/svg/OnTheClockIcon';

export interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void;
  sortAsc: boolean;
  sortBy: string;
  headers: HeadCell[];
}

export default function EnhancedTableHead(props: EnhancedTableProps) {
  const { sortAsc, sortBy, onRequestSort } = props;
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const createSortHandler =
    (property: string) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead sx={{ zIndex: 3 }}>
      <TableRow
        sx={{
          zIndex: 3,
          '& .MuiTableCell-root': {
            borderBottom: '1px solid',
            borderColor: theme.palette.supportingAdditional.tealContainerStroke,
          },
        }}
      >
        <TableCell
          key={`row-num-showDetail`}
          align="center"
          padding="normal"
          sx={{
            zIndex: 900,
            left: 0,
            maxWidth: '230px',
            minWidth: '44px',
            maxHeight: '76px',
            minHeight: '76px',
          }}
        ></TableCell>
        {props.headers
          .filter((e) => e.visible)
          .map((headCell) => (
            <TableCell
              key={headCell.id}
              align={'left'}
              padding="normal"
              sortDirection={sortBy === headCell.key && sortAsc ? 'asc' : false}
              sx={
                headCell.id === 'id'
                  ? {
                      zIndex: 900,
                      left: 44,
                      maxWidth: '250px',
                      minWidth: '250px',
                    }
                  : headCell.id === 'taskProgress'
                    ? {
                        zIndex: 880,
                        textWrap: 'nowrap',
                        p: 0,
                        backgroundColor: theme.palette.supportingAdditional.tealUltraLight,
                        borderLeft: '1px solid',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                      }
                    : { zIndex: 880, textWrap: 'nowrap' }
              }
            >
              <TableSortLabel
                active={sortBy === headCell.key}
                direction={sortBy === headCell.key && sortAsc ? 'asc' : 'desc'}
                aria-label={`Table Sort Label ${headCell.key}`}
                onClick={createSortHandler(headCell.key)}
                IconComponent={() => (
                  <SortIcons
                    sort={sortAsc}
                    property={sortBy === headCell.key}
                    isVisible={headCell.key !== ''}
                  />
                )}
                disabled={headCell.key === ''}
                sx={{
                  color: isDarkMode ? theme.palette.text.primary : theme.palette.content.default,
                  fontWeight: theme.typography.fontWeightBold,
                  '&.Mui-disabled': {
                    color: isDarkMode ? theme.palette.text.primary : theme.palette.content.default,
                  },
                  ...(headCell.id === 'taskProgress'
                    ? { alignSelf: 'stretch', display: 'flex', width: '100%' }
                    : {}),
                }}
              >
                {headCell.id === 'taskProgress' ? (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                      alignItems: 'center',
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      minHeight: '64px',
                      boxSizing: 'border-box',
                      px: 2.5,
                      py: 2,
                      backgroundColor: theme.palette.supportingAdditional.tealUltraLight,
                      borderTopLeftRadius: '6px',
                      borderTopRightRadius: '6px',
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Chip
                        label="You"
                        size="small"
                        sx={{
                          color: theme.palette.content.white,
                          backgroundColor: theme.palette.prime.orange,
                          transform: 'scale(1.3)',
                          border: '1px solid',
                          borderColor: theme.palette.primeAdditional.lightOrange,
                          boxShadow: '0 1px 3px rgba(18, 57, 93, 0.24)',
                          fontWeight: theme.typography.fontWeightBold,
                        }}
                        icon={<OnTheClockIcon color={theme.palette.content.white} />}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        left: '50%',
                        position: 'absolute',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        whiteSpace: 'nowrap',
                        fontSize: '1.2em',
                        fontWeight: theme.typography.fontWeightBold,
                        color: theme.palette.content.default,
                      }}
                    >
                      On the Clock
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Chip
                        label="Others"
                        size="small"
                        sx={{
                          color: theme.palette.content.white,
                          backgroundColor: theme.palette.prime.granite,
                          transform: 'scale(1.2)',
                          border: '1px solid',
                          borderColor: theme.palette.content.white,
                          boxShadow: '0 1px 3px rgba(18, 57, 93, 0.24)',
                          fontWeight: theme.typography.fontWeightBold,
                        }}
                        icon={<OnTheClockIcon color={theme.palette.content.white} />}
                      />
                    </Box>
                  </Box>
                ) : (
                  <Typography
                    component="span"
                    sx={{ color: isDarkMode ? theme.palette.text.primary : theme.palette.content.default, fontWeight: theme.typography.fontWeightBold }}
                  >
                    {headCell.label}
                  </Typography>
                )}
              </TableSortLabel>
            </TableCell>
          ))}
      </TableRow>
    </TableHead>
  );
}
